var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { connect, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import cx from "classnames";
import Ajv from "ajv";
import { BifIconCheckThin, BifIconCrossThin, BifIconNote, BifIconPin, BifIconNoteFile, BifIconInformationCircle, Button, Form, Row, Col, FormControl, OverlayTrigger, Tooltip } from "banafo-design-system";
import { pauseAudio } from "src/features/auth/store/actions/playback";
import { selectRecordingDetailsByCallId } from "src/features/auth/store/selectors/recordingDetails";
import { NOTE_TYPE_TIME } from "src/features/auth/config/notes";
import { selectNoteById, useAddNoteMutation, useEditNoteMutation } from "src/features/notes/notesSlice";
import { useParams } from "react-router-dom";
import "./NotesControls.scss";
import { MODE_ADD, MODE_EDIT, MODE_VIEW } from "src/features/notes/config";
const ajv = new Ajv({ allErrors: true, jsonPointers: true, $data: true });
require("ajv-errors")(ajv /*, {singleError: true} */);
const schema = {
    type: "object",
    required: ["noteDescription"],
    properties: {
        "noteDescription": {
            "type": "string",
            "minLength": 1
        }
    }
};
function NotesControls({ playbackCurrentTime, playbackDuration, playbackPlaying, mode, setMode, activeNote, setSavedActiveNote, pauseAudio, descriptionInput, setDescriptionInput, noteTypeInput, setNoteTypeInput, noteId, errors, setErrors }) {
    var _a;
    const { t } = useTranslation();
    const { callId } = useParams();
    const pinButton = useRef(null);
    const textareaRef = useRef(null);
    const [addNote, { isLoading: isAddingNote }] = useAddNoteMutation();
    const [editNote, { isLoading: isEditingNote }] = useEditNoteMutation();
    const note = useSelector(state => selectNoteById(state, callId, noteId));
    const recording = useSelector(state => selectRecordingDetailsByCallId(state)(callId));
    const validateForm = () => {
        if (!ajv.validate(schema, Object.assign({ "noteDescription": descriptionInput }))) {
            const errors = ajv.errors.map(error => ({ name: error.dataPath.substr(1), message: error.message }))
                .reduce((acc, error) => {
                acc[error.name] = error.message;
                return acc;
            }, {});
            setErrors(errors);
            return false;
        }
        return true;
    };
    const handlePlacePin = () => {
        let cuePoint = null;
        if (recording) {
            const currentTime = playbackCurrentTime * 1000;
            const { startedAt: recordingStartedAt } = recording;
            cuePoint = recordingStartedAt + currentTime;
        }
        addNote({
            title: "N/A",
            description: "",
            parentType: "call",
            parentId: callId,
            createdWhile: "recording",
            cuePoint
        })
            .unwrap()
            .catch(e => null)
            .finally(() => {
            pinButton.current.blur();
        });
    };
    const handleNewNote = () => {
        recording && pauseAudio();
        setMode(MODE_ADD);
        setSavedActiveNote(undefined);
        setNoteTypeInput(NOTE_TYPE_TIME);
        setDescriptionInput("");
        setErrors({});
    };
    const isViewMode = mode === MODE_VIEW;
    const currentTime = playbackCurrentTime * 1000;
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.focus();
        }
    }, [noteId, mode]);
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: cx("Notes-note-controls"), children: [_jsx("div", { className: "Notes-spacer" }), _jsxs("div", { className: "d-none", children: [_jsx(OverlayTrigger, { placement: "top", overlay: _jsx(Tooltip, { id: "open-note", children: t("New.point.of.interest_") }), children: _jsx(Button, { id: "new-pin-button", as: "a", variant: "notice", size: "sm", className: "me-50", onClick: handlePlacePin, ref: pinButton, children: _jsx(BifIconPin, {}) }) }), _jsx(OverlayTrigger, { placement: "top", overlay: _jsx(Tooltip, { id: "open-note", children: t("New.note_") }), children: _jsx(Button, { id: "new-note-button", variant: "tertiary", size: "sm", className: "me-50", onClick: handleNewNote, children: _jsx(BifIconNoteFile, {}) }) })] })] }), !isViewMode && _jsx(Row, { className: "p-50 bg-light", children: _jsx(Col, { xs: 12, children: _jsxs("div", { className: "input-group", children: [_jsx("div", { className: "me-100", children: ((_a = note === null || note === void 0 ? void 0 : note.description) === null || _a === void 0 ? void 0 : _a.length) ? _jsx(BifIconNoteFile, { variant: "dark" }) : mode === MODE_EDIT ? _jsx(BifIconPin, { variant: "dark" }) : _jsx(BifIconNoteFile, { variant: "dark" }) }), _jsxs("div", { className: "me-100 w-50", children: [_jsx(FormControl, { as: "textarea", rows: 3, placeholder: `${t("Type.your.note.here_")}...`, value: descriptionInput, className: "me-100", name: "noteDescription", isInvalid: !!errors.noteDescription, onChange: e => setDescriptionInput(e.target.value), ref: textareaRef, onFocus: (e) => e.currentTarget.setSelectionRange(e.currentTarget.value.length, e.currentTarget.value.length) }), _jsx(Form.Control.Feedback, { type: "invalid", children: errors.noteDescription }), _jsxs("p", { className: "alert alert-dark mt-50 mb-50", children: [_jsx(BifIconInformationCircle, { className: "me-50" }), _jsx("small", { children: t('infos:Change.note.location.in.time_') })] })] }), _jsx("div", { className: "me-100", children: _jsx(FormControl, { type: "text", size: "sm", className: "note-time", disabled: true, placeholder: moment(Math.round(currentTime)).format("mm:ss") }) }), mode !== MODE_VIEW && _jsxs("div", { className: "ms-auto", children: [_jsx(OverlayTrigger, { placement: "top", overlay: _jsx(Tooltip, { id: "open-note", children: t("Save_") }), children: _jsx(Button, { variant: "link", size: "sm", className: "text-dark", onClick: () => __awaiter(this, void 0, void 0, function* () {
                                                if (!validateForm()) {
                                                    return;
                                                }
                                                let cuePoint = null;
                                                if (recording) {
                                                    const currentTime = playbackCurrentTime * 1000;
                                                    const { startedAt: recordingStartedAt } = recording;
                                                    cuePoint = recordingStartedAt + currentTime;
                                                }
                                                if (mode === MODE_ADD) {
                                                    yield addNote({
                                                        title: "N/A",
                                                        description: descriptionInput,
                                                        parentType: "call",
                                                        parentId: callId,
                                                        createdWhile: "recording",
                                                        cuePoint
                                                    });
                                                }
                                                else {
                                                    yield editNote(Object.assign(Object.assign({}, note), { description: descriptionInput, cuePoint // TODO handle the case where the cuePoint hasn't been changed
                                                     }));
                                                }
                                                setMode(MODE_VIEW);
                                            }), children: _jsx(BifIconCheckThin, {}) }) }), _jsx(OverlayTrigger, { placement: "top", overlay: _jsx(Tooltip, { id: "open-note", children: t("Cancel_") }), children: _jsx(Button, { variant: "link", size: "sm", className: "me-50 text-dark", onClick: () => {
                                                setMode(MODE_VIEW);
                                                setNoteTypeInput(NOTE_TYPE_TIME);
                                            }, children: _jsx(BifIconCrossThin, {}) }) })] })] }) }) })] }));
}
function mapStateToProps(state, ownProps) {
    return {
        playbackCurrentTime: state.app.playback.currentTime,
        playbackDuration: state.app.playback.duration,
        playbackPlaying: state.app.playback.playing,
    };
}
const mapDispatchToProps = {
    pauseAudio,
};
export default connect(mapStateToProps, mapDispatchToProps)(NotesControls);
NotesControls.propTypes = {
    activeNote: PropTypes.any,
    mode: PropTypes.any,
    pauseAudio: PropTypes.any,
    playbackCurrentTime: PropTypes.any,
    playbackDuration: PropTypes.any,
    playbackPlaying: PropTypes.bool,
    setMode: PropTypes.any,
    setSavedActiveNote: PropTypes.any,
    descriptionInput: PropTypes.any,
    setDescriptionInput: PropTypes.any,
    noteTypeInput: PropTypes.any,
    setNoteTypeInput: PropTypes.func,
    noteId: PropTypes.any,
    errors: PropTypes.any,
    setErrors: PropTypes.func
};
